/**
 * ContractsQualityHub — Page Qualité & Risques unifiée avec 4 onglets internes.
 *
 * Regroupe tous les outils de contrôle qualité et risques :
 *   ┌─ Conformité    (clauses obligatoires, RGPD, mentions légales)
 *   ├─ Anomalies     (montants incohérents, dates incompatibles, doublons)
 *   ├─ Vigilance     (impayés, dépendance, alertes financières)
 *   └─ Indexation    (révision des indices, INSEE)
 *
 * Onglet actif via ?tab= pour deep-links.
 */
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ShieldCheck, AlertTriangle, ShieldAlert, TrendingUp } from "lucide-react";
import ContratsConformite from "@/pages/ContratsConformite";
import ContratsAnomalies from "@/pages/ContratsAnomalies";
import VigilanceContrats from "@/pages/VigilanceContrats";
import ContratsIndexation from "@/pages/ContratsIndexation";

const TAB_ALIASES = {
  conformite: "conformite", compliance: "conformite",
  anomalies: "anomalies",
  vigilance: "vigilance",
  indexation: "indexation", indices: "indexation",
};

export default function ContractsQualityHub() {
  const [params, setParams] = useSearchParams();
  const active = TAB_ALIASES[params.get("tab")] || "conformite";

  const setTab = (t) => {
    const p = new URLSearchParams(params);
    if (t === "conformite") p.delete("tab");
    else p.set("tab", t);
    setParams(p, { replace: true });
  };

  return (
    <div className="space-y-6" data-testid="contracts-quality-hub">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900"
            style={{ fontFamily: "Manrope, sans-serif" }}>
          Qualité & Risques
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Contrôle des clauses, détection des anomalies IA, alertes financières et révisions d'indices.
        </p>
      </div>

      <Tabs value={active} onValueChange={setTab}>
        <TabsList className="bg-slate-100 p-1 h-auto">
          <TabsTrigger value="conformite" data-testid="quality-tab-conformite"
                       className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2 px-4 py-2">
            <ShieldCheck className="h-4 w-4" />
            Conformité
          </TabsTrigger>
          <TabsTrigger value="anomalies" data-testid="quality-tab-anomalies"
                       className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2 px-4 py-2">
            <AlertTriangle className="h-4 w-4" />
            Anomalies
          </TabsTrigger>
          <TabsTrigger value="vigilance" data-testid="quality-tab-vigilance"
                       className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2 px-4 py-2">
            <ShieldAlert className="h-4 w-4" />
            Vigilance
          </TabsTrigger>
          <TabsTrigger value="indexation" data-testid="quality-tab-indexation"
                       className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2 px-4 py-2">
            <TrendingUp className="h-4 w-4" />
            Indexation
          </TabsTrigger>
        </TabsList>

        <TabsContent value="conformite" className="mt-6"><ContratsConformite embedded /></TabsContent>
        <TabsContent value="anomalies" className="mt-6"><ContratsAnomalies embedded /></TabsContent>
        <TabsContent value="vigilance" className="mt-6"><VigilanceContrats embedded /></TabsContent>
        <TabsContent value="indexation" className="mt-6"><ContratsIndexation embedded /></TabsContent>
      </Tabs>
    </div>
  );
}
